import { getListingImageId, isQuarterTurn, rotateImage } from '@/helpers/imageRotation';
import type { RotationDirection } from '@/helpers/imageRotation';
import { useRotateListingImageMutation } from '@/store/api/services/marketing/marketing';
import type { ListingImage } from '@/types';
import { usePermissions } from '@bitstopco/bitstop-theme';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const STAGE_HEIGHT = '60vh';

interface ListingMediaLightboxProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  title: string;
  images: ListingImage[];
  initialIndex?: number;
  scope?: 'listing' | 'location';
}

const ListingMediaLightbox = ({
  open,
  onClose,
  locationId,
  title,
  images,
  initialIndex = 0,
  scope = 'listing',
}: ListingMediaLightboxProps) => {
  const canRotate = usePermissions({ permission: 'locations.modify' })[1] ?? false;
  const [rotateListingImage] = useRotateListingImageMutation();
  const [index, setIndex] = useState(0);

  const total = images.length;
  const current = images[Math.min(index, total - 1)];
  const currentId = current ? getListingImageId(current) : undefined;
  const rotation = current?.rotation ?? 0;
  const quarterTurn = isQuarterTurn(rotation);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const goTo = (step: number) => setIndex((prev) => (prev + step + total) % total);

  const handleRotate = async (direction: RotationDirection) => {
    if (!currentId) return;
    try {
      await rotateListingImage({
        locationId,
        imageId: currentId,
        rotation: rotateImage(rotation, direction),
        scope,
      }).unwrap();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to save image rotation.');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (total < 2) return;
    if (event.key === 'ArrowLeft') goTo(-1);
    if (event.key === 'ArrowRight') goTo(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      onClick={(event) => event.stopPropagation()}
      onKeyDown={handleKeyDown}
      data-test="listing-media-lightbox"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Typography variant="h6" noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        {total > 0 && (
          <Typography variant="body2" color="text.secondary" data-test="listing-media-counter">
            {Math.min(index, total - 1) + 1} / {total}
          </Typography>
        )}
        {canRotate && current && (
          <>
            <Tooltip title="Rotate left">
              <span>
                <IconButton
                  onClick={() => handleRotate('left')}
                  disabled={!currentId}
                  data-test="listing-media-rotate-left"
                >
                  <RotateLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rotate right">
              <span>
                <IconButton
                  onClick={() => handleRotate('right')}
                  disabled={!currentId}
                  data-test="listing-media-rotate-right"
                >
                  <RotateRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <IconButton onClick={onClose} aria-label="Close" data-test="listing-media-close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            height: STAGE_HEIGHT,
            containerType: 'inline-size',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            bgcolor: 'action.hover',
            borderRadius: 2,
          }}
        >
          {!current && (
            <Typography color="text.secondary">No media available for this listing.</Typography>
          )}
          {current && (
            <Box
              component="img"
              key={current.url}
              src={current.url}
              alt={`${title} ${index + 1}`}
              data-test="listing-media-image"
              data-rotation={rotation}
              sx={{
                maxWidth: quarterTurn ? STAGE_HEIGHT : '100cqw',
                maxHeight: quarterTurn ? '100cqw' : STAGE_HEIGHT,
                objectFit: 'contain',
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease',
              }}
            />
          )}
          {total > 1 && (
            <>
              <IconButton
                onClick={() => goTo(-1)}
                aria-label="Previous image"
                data-test="listing-media-prev"
                sx={{ position: 'absolute', left: 8, bgcolor: 'background.paper' }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => goTo(1)}
                aria-label="Next image"
                data-test="listing-media-next"
                sx={{ position: 'absolute', right: 8, bgcolor: 'background.paper' }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
        </Box>
        {total > 1 && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, overflowX: 'auto', pb: 1 }}>
            {images.map((image, i) => (
              <Box
                key={image.url}
                onClick={() => setIndex(i)}
                sx={{
                  flexShrink: 0,
                  width: 64,
                  height: 64,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: 2,
                  borderColor: i === index ? 'primary.main' : 'transparent',
                }}
              >
                <Box
                  component="img"
                  src={image.url}
                  alt={`${title} thumbnail ${i + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: `rotate(${image.rotation ?? 0}deg)`,
                  }}
                />
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ListingMediaLightbox;
